import { isResponseError } from '@repo/upfetch'

import type { CompanyResult } from './company-result-row.tsx'
import {
  type CompanySearchResult,
  toCompanyAddressLines,
  toCompanyResult,
  toCompanyStatistics,
} from './company-search-result.ts'

export type CompanyPickerCompany = CompanyResult & {
  id: number
  organisation?: CompanyOrganisation | null
  representsOrganisationId?: null | number
  typeAlias?: CompanyTypeAlias
  xeroContactUrl?: null | string
}

export type CompanyTypeAlias = 'broker' | 'haulier' | 'other' | 'shipper'

type CompanyOrganisation = { name: string; slug: string }

type CompanyPickerSource = {
  address?: null | {
    address?: null | string
    address_second_line?: null | string
    city?: null | string
    place_name?: null | string
    postcode?: null | string
  }
  country?: null | {
    code?: null | string
    name?: null | string
  }
  id: number
  is_marketplace?: boolean
  organisation?: CompanyOrganisation | null
  represents_organisation_id?: null | number
  risk_profile?: null | {
    score?: null | string
    valid_to?: null | string
  }
  statistics?: null | {
    carrier_jobs_count: number
    contacts_count: number
    customer_loads_count: number
    locations_count: number
  }
  title: string
  type?: null | string | { alias?: string }
  vat?: null | string
  xero_attributes?: null | { xero_contact_id?: null | string; xero_contact_url?: null | string }
  xero_contact_url?: null | string
}

const isCompanyTypeAlias = (value: string | undefined): value is CompanyTypeAlias =>
  value === 'broker' || value === 'haulier' || value === 'other' || value === 'shipper'

/** The API's message for a failed search, else `fallback`, which names what failed to load. */
export const getCompanyPickerError = (error: unknown, fallback = 'Unable to load companies.') => {
  if (!error) return undefined
  if (isResponseError(error)) return error.data.message

  return fallback
}

export const getCompanyPickerStatus = ({
  isFetching,
  isPending,
}: {
  isFetching: boolean
  isPending: boolean
}): 'loading' | 'pending' | 'ready' => {
  if (isPending) return 'pending'
  if (isFetching) return 'loading'

  return 'ready'
}

export const toCompanyPickerCompany = (
  company: CompanyPickerSource,
  { includeRisk = false }: { includeRisk?: boolean } = {},
): CompanyPickerCompany => {
  const companyType = typeof company.type === 'string' ? company.type : company.type?.alias
  const typeAlias = isCompanyTypeAlias(companyType) ? companyType : undefined
  const placeName = company.address?.place_name
  const addressLines = placeName ? [placeName] : toCompanyAddressLines(company.address)
  let riskProfile: CompanyPickerCompany['riskProfile'] = undefined

  if (includeRisk && company.risk_profile !== undefined) {
    if (company.risk_profile === null) {
      riskProfile = null
    } else {
      riskProfile = {
        score: company.risk_profile.score,
        validTo: company.risk_profile.valid_to,
      }
    }
  }

  const xeroContactUrl = company.xero_attributes?.xero_contact_url ?? company.xero_contact_url

  return {
    addressLines: addressLines.length ? addressLines : undefined,
    country: company.country,
    id: company.id,
    isBroker: typeAlias === 'broker',
    isMarketplace: company.is_marketplace,
    // Matches `xero_synced` in company search, which needs only the Xero contact.
    isXeroSynced: Boolean(company.xero_attributes?.xero_contact_id || xeroContactUrl),
    organisation: company.organisation,
    organisationSlug: company.organisation?.slug,
    representsOrganisationId: company.represents_organisation_id,
    riskProfile,
    statistics: company.statistics ? toCompanyStatistics(company.statistics) : undefined,
    title: company.title,
    typeAlias,
    vat: company.vat,
    xeroContactUrl,
  }
}

/** Shapes a `GET /companies/search` result for the admin company pickers. */
export const fromCompanySearchResult = (
  result: CompanySearchResult,
  options: { includeRisk?: boolean } = {},
): CompanyPickerCompany => ({
  ...toCompanyResult(result, options),
  id: result.id,
  representsOrganisationId: result.represents_organisation_id,
  typeAlias: result.type ?? undefined,
})
